"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useState } from "react";
import { AuthField } from "~/components/auth/AuthField";
import { AuthPanel } from "~/components/auth/AuthPanel";
import { Button } from "~/components/ui/button";
import { Paragraph } from "~/components/ui/Typography";
import { authClient } from "~/lib/auth/client";
import { api } from "~/trpc/react";
type NameStepProps = {
  initialFirstName: string;
  initialLastName: string;
  onComplete: () => void;
  onBack: () => void;
};
type NameErrors = {
  firstName?: string;
  lastName?: string;
  terms?: string;
  form?: string;
};
export function NameStep(t0) {
  const $ = _c(48);
  const {
    initialFirstName,
    initialLastName,
    onComplete,
    onBack
  } = t0;
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [errors, setErrors] = useState(t1);
  const [showConfirm, setShowConfirm] = useState(false);
  const updateUser = api.user.updateUser.useMutation();
  let t2;
  if ($[1] !== errors.terms) {
    t2 = e => {
      setAcceptedTerms(e.target.checked);
      if (errors.terms) {
        setErrors(_temp);
      }
    };
    $[1] = errors.terms;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTermsChange = t2;
  let t3;
  if ($[3] !== acceptedTerms || $[4] !== firstName || $[5] !== lastName || $[6] !== onComplete || $[7] !== updateUser) {
    t3 = async () => {
      const nextErrors = {
        firstName: firstName.trim() ? undefined : "First name is required.",
        lastName: lastName.trim() ? undefined : "Last name is required.",
        terms: acceptedTerms ? undefined : "You must agree to the terms to continue."
      };
      setErrors(nextErrors);
      if (Object.values(nextErrors).some(Boolean)) {
        return;
      }
      try {
        await updateUser.mutateAsync({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          driver_terms_accepted: true
        });
        await authClient.refresh();
        onComplete();
      } catch {
        setErrors({
          form: "Something went wrong. Please try again."
        });
      }
    };
    $[3] = acceptedTerms;
    $[4] = firstName;
    $[5] = lastName;
    $[6] = onComplete;
    $[7] = updateUser;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleContinue = t3;
  if (showConfirm) {
    let t4;
    if ($[9] !== onBack) {
      t4 = <Button className="mb-3 w-full" variant="primary" size="lg" onClick={onBack} type="button">Sign out</Button>;
      $[9] = onBack;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Button className="w-full" variant="ghost" size="sm" onClick={() => setShowConfirm(false)} type="button">Stay</Button>;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== t4) {
      t6 = <AuthPanel title="Sign out?" subtitle="You'll be signed out of your account.">{t4}{t5}</AuthPanel>;
      $[12] = t4;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  let t4;
  if ($[14] !== errors.firstName || $[15] !== firstName) {
    t4 = <AuthField id="first-name" label="First name" placeholder="Joe" autoComplete="given-name" value={firstName} error={errors.firstName} onChange={setFirstName} />;
    $[14] = errors.firstName;
    $[15] = firstName;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== errors.lastName || $[18] !== lastName) {
    t5 = <AuthField id="last-name" label="Last name" placeholder="Easy" autoComplete="family-name" value={lastName} error={errors.lastName} onChange={setLastName} />;
    $[17] = errors.lastName;
    $[18] = lastName;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4 || $[21] !== t5) {
    t6 = <div className="mb-3 flex flex-col gap-2.5 sm:mb-4 sm:gap-3"><div className="grid gap-2.5 sm:grid-cols-2 sm:gap-3">{t4}{t5}</div></div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== acceptedTerms || $[24] !== handleTermsChange) {
    t7 = <input checked={acceptedTerms} className="mt-1 h-[15px] w-[15px] shrink-0 accent-accent" id="accepted-terms" onChange={handleTermsChange} type="checkbox" />;
    $[23] = acceptedTerms;
    $[24] = handleTermsChange;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Link className="font-semibold text-accent-dark hover:underline" href="/terms" target="_blank">Terms of Service</Link>;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <label htmlFor="accepted-terms" className="min-w-0"><Paragraph size="sm" className="sm:text-[14px]">I agree to EZSpot's{" "}{t8}{" "}and{" "}<Link className="font-semibold text-accent-dark hover:underline" href="/privacy" target="_blank">Privacy Policy</Link>.</Paragraph></label>;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t7) {
    t10 = <div className="mb-2 flex items-start gap-2.5">{t7}{t9}</div>;
    $[28] = t7;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== errors.terms) {
    t11 = errors.terms ? <Paragraph size="sm" className="mb-3 text-danger sm:mb-4">{errors.terms}</Paragraph> : null;
    $[30] = errors.terms;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== errors.form) {
    t12 = errors.form ? <Paragraph size="sm" className="mb-3 text-danger sm:mb-4">{errors.form}</Paragraph> : null;
    $[32] = errors.form;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = updateUser.isPending ? "Saving..." : "Continue";
  let t14;
  if ($[34] !== handleContinue || $[35] !== t13 || $[36] !== updateUser.isPending) {
    t14 = <Button className="mb-3 w-full" disabled={updateUser.isPending} onClick={handleContinue} type="button" variant="primary" size="lg">{t13}</Button>;
    $[34] = handleContinue;
    $[35] = t13;
    $[36] = updateUser.isPending;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setShowConfirm(true);
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== updateUser.isPending) {
    t16 = <Button className="w-full" variant="ghost" size="sm" onClick={t15} type="button" disabled={updateUser.isPending}>Back</Button>;
    $[39] = updateUser.isPending;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t14 || $[45] !== t16 || $[46] !== t6) {
    t17 = <AuthPanel title="What's your name?" subtitle="Let us know what to call you">{t6}{t10}{t11}{t12}{t14}{t16}</AuthPanel>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t14;
    $[45] = t16;
    $[46] = t6;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
}
function _temp(prev) {
  return {
    ...prev,
    terms: undefined
  };
}